const KB = 1024;

export function formatBytes(bytes: number): string {
  if (bytes < KB) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / KB;
  let unitIndex = 0;
  while (value >= KB && unitIndex < units.length - 1) {
    value /= KB;
    unitIndex += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unitIndex] ?? 'TB'}`;
}

/**
 * The owner's date preferences (Settings → General), applied to every
 * timestamp this module formats.
 *
 * Module state rather than a React context, because these are plain functions
 * called from deep inside list rows: threading a locale through every caller
 * would touch every view for one setting. `PreferencesProvider` calls
 * {@link configureDateFormat} when the preferences load or change; until then,
 * and whenever a value is empty, the browser's own locale and zone apply,
 * which is exactly what this module did before the setting existed.
 */
export interface DateFormatPreferences {
  /** BCP 47 tag; empty or absent means the browser's own. */
  locale?: string;
  /** IANA zone; empty or absent means the device's own. */
  timeZone?: string;
}

interface Formatters {
  time: Intl.DateTimeFormat;
  day: Intl.DateTimeFormat;
  full: Intl.DateTimeFormat;
  long: Intl.DateTimeFormat;
  /** "Tue 2:30 PM": a moment within the coming week. */
  weekdayTime: Intl.DateTimeFormat;
  /** `YYYY-MM-DD` in the chosen zone, for "is this today / this year". */
  calendarDay: Intl.DateTimeFormat;
  /** `HH:MM` (24-hour) in the chosen zone: a wall-clock time. */
  wallTime: Intl.DateTimeFormat;
}

/**
 * One formatter, falling back to the runtime defaults when the locale or zone
 * is one this runtime does not know: a bad preference must not take down every
 * list that renders a date.
 */
function makeFormat(
  locale: string | undefined,
  zone: { timeZone?: string },
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat(locale, { ...options, ...zone });
  } catch {
    return new Intl.DateTimeFormat(locale === 'en-CA' ? 'en-CA' : undefined, options);
  }
}

function buildFormatters(locale: string, timeZone: string): Formatters {
  // An empty value is `undefined` to Intl, i.e. "the runtime's own".
  const loc = locale === '' ? undefined : locale;
  const zone = timeZone === '' ? {} : { timeZone };
  return {
    time: makeFormat(loc, zone, { hour: 'numeric', minute: '2-digit' }),
    day: makeFormat(loc, zone, { month: 'short', day: 'numeric' }),
    full: makeFormat(loc, zone, { year: 'numeric', month: 'short', day: 'numeric' }),
    long: makeFormat(loc, zone, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }),
    weekdayTime: makeFormat(loc, zone, { weekday: 'short', hour: 'numeric', minute: '2-digit' }),
    // en-CA writes YYYY-MM-DD, which compares as a plain string.
    calendarDay: makeFormat('en-CA', zone, { year: 'numeric', month: '2-digit', day: '2-digit' }),
    wallTime: makeFormat('en-GB', zone, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
  };
}

let current = { locale: '', timeZone: '' };
let formatters = buildFormatters('', '');

/**
 * Applies the owner's locale and zone to every formatter here. Idempotent and
 * cheap when nothing changed, so a caller may run it on every render.
 */
export function configureDateFormat(prefs: DateFormatPreferences): void {
  const next = { locale: prefs.locale ?? '', timeZone: prefs.timeZone ?? '' };
  if (next.locale === current.locale && next.timeZone === current.timeZone) return;
  current = next;
  formatters = buildFormatters(next.locale, next.timeZone);
}

/**
 * Gmail-style: time for today, "Mar 4" this year, full date otherwise.
 *
 * "Today" is today in the chosen zone, not the device's: a message that
 * arrived at 23:30 in Sydney is not "today" in London because the laptop says
 * so.
 */
export function formatListDate(iso: string): string {
  const date = new Date(iso);
  const day = formatters.calendarDay.format(date);
  const today = formatters.calendarDay.format(new Date());
  if (day === today) return formatters.time.format(date);
  if (day.slice(0, 4) === today.slice(0, 4)) return formatters.day.format(date);
  return formatters.full.format(date);
}

/** Today as `YYYY-MM-DD`, in the chosen zone (a task's "due today", ADR 0069). */
export function todayCalendarDay(now: Date = new Date()): string {
  return formatters.calendarDay.format(now);
}

/**
 * The wall-clock time now, `HH:MM`, in the chosen zone — what "later today"
 * is counted from (ADR 0075), the same zone a task's due time is read in.
 */
export function nowWallTime(now: Date = new Date()): string {
  return formatters.wallTime.format(now);
}

export function formatFullDate(iso: string): string {
  return formatters.long.format(new Date(iso));
}

/*
 * The helpers below are for everything that is not a message date: session
 * sign-ins, privacy rules, devices, snoozes, drafts, calendar links. Before
 * #343 those called `toLocaleDateString()` directly, so Settings → General's
 * "everywhere in the app" was true only of the mail list and reading pane.
 */

type DateInput = string | number | Date;

/** "Sep 18, 2026" — a day, in the chosen locale and zone. */
export function formatDate(when: DateInput): string {
  return formatters.full.format(new Date(when));
}

/** "Fri, Sep 18, 2026, 9:00 AM" — a moment, in the chosen locale and zone. */
export function formatDateTime(when: DateInput): string {
  return formatters.long.format(new Date(when));
}

/** "Tue 2:30 PM" — a moment in the coming week, in the chosen locale and zone. */
export function formatWeekdayTime(when: DateInput): string {
  return formatters.weekdayTime.format(new Date(when));
}

/** "9:00 AM" — a time of day, in the chosen locale and zone. */
export function formatTime(when: DateInput): string {
  return formatters.time.format(new Date(when));
}

/**
 * The chosen locale for code that keeps its own `Intl.DateTimeFormat` cache
 * (the calendar): `undefined` means the browser's own.
 */
export function dateLocale(): string | undefined {
  return current.locale === '' ? undefined : current.locale;
}

/**
 * The owner's zone as an IANA name: Settings → General when it is set and
 * this runtime knows it, else the device's own. What wall-clock choices
 * ("tomorrow at 8:00") are made in (ADR 0076).
 */
export function ownerTimeZone(): string {
  if (isKnownTimeZone(current.timeZone)) return current.timeZone;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Whether this runtime knows an IANA zone; a bad preference must never throw. */
export function isKnownTimeZone(zone: string): boolean {
  if (zone === '') return false;
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

export function displayName(address: { name?: string; email: string }): string {
  return address.name && address.name.trim().length > 0 ? address.name : address.email;
}
